import React, { useState } from 'react';
import './PreviewFeedbackForm.css';

interface PreviewFeedbackFormProps {
  bookId: string;
  pathName: string;
  completedPaths: string[];
  onSubmit: (feedback: FeedbackData) => Promise<void>;
  onTryAnotherPath: () => void;
  onBackToHome: () => void;
}

interface FeedbackData {
  rating: number;
  comments: string;
  email: string;
  bookId: string;
  pathName: string;
}

export default function PreviewFeedbackForm({ 
  bookId, 
  pathName, 
  completedPaths,
  onSubmit,
  onTryAnotherPath,
  onBackToHome
}: PreviewFeedbackFormProps) {
  const [rating, setRating] = useState<number>(0);
  const [comments, setComments] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Prevent double submission
    
    setIsSubmitting(true);
    setSubmitError('');
    
    const feedbackData: FeedbackData = {
      rating,
      comments,
      email,
      bookId,
      pathName
    };

    try {
      await onSubmit(feedbackData);
      setSubmitted(true);
    } catch (error) {
      setSubmitError('Failed to submit feedback. Please try again.');
      console.error('Feedback submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUnexploredPaths = () => {
    const allPaths = ['Detective', 'Conspirator', 'Scientist'];
    return allPaths.filter(path => !completedPaths.includes(path));
  };

  const unexploredPaths = getUnexploredPaths();

  if (submitted) {
    return (
      <div className="feedback-thank-you">
        <div className="feedback-container">
          <h2>Thank you for your feedback! 🎉</h2>
          <p>Your thoughts help shape future stories and chapters.</p>
          
          {email && (
            <div className="notification-signup-success">
              <p>✉️ We'll keep you updated on new chapters and stories at:</p>
              <strong>{email}</strong>
            </div>
          )}

          <div className="next-steps-section">
            <h3>What would you like to do next?</h3>
            
            {unexploredPaths.length > 0 && (
              <div className="try-other-paths">
                <p>Want to see how the story unfolds differently?</p>
                <div className="unexplored-paths-preview">
                  {unexploredPaths.map(path => (
                    <div key={path} className="path-preview">
                      <strong>{path}:</strong> {getPathDescription(path)}
                    </div>
                  ))}
                </div>
                <button 
                  className="try-another-path-btn"
                  onClick={onTryAnotherPath}
                >
                  Start over and try the {unexploredPaths.join(' or ')} path
                </button>
              </div>
            )}

            <div className="explore-more">
              <p>Or discover completely new stories:</p>
              <button 
                className="back-to-home-btn"
                onClick={onBackToHome}
              >
                Explore Other Stories
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="preview-feedback-form">
      <div className="feedback-container">
        <div className="feedback-header">
          <h2>You've experienced the first part of your journey. What did you think?</h2>
          <p className="path-completed">You completed the <strong>{pathName}</strong> path preview</p>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          {/* Star Rating */}
          <div className="rating-section">
            <label>How would you rate this experience?</label>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`star ${star <= rating ? 'filled' : ''}`}
                  onClick={() => handleStarClick(star)}
                  aria-label={`Rate ${star} stars`}
                >
                  ★
                </button>
              ))}
            </div>
            {rating > 0 && (
              <span className="rating-text">
                {rating === 1 && "Could be better"}
                {rating === 2 && "It was okay"}
                {rating === 3 && "Pretty good"}
                {rating === 4 && "Really enjoyed it"}
                {rating === 5 && "Absolutely loved it!"}
              </span>
            )}
          </div>

          {/* Comments */}
          <div className="comments-section">
            <label htmlFor="comments">Any thoughts or suggestions?</label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="What did you like? What could be improved? Any suggestions for future stories?"
              rows={4}
            />
          </div>

          {/* Email Signup */}
          <div className="email-section">
            <label htmlFor="email">Stay updated on new chapters and stories</label>
            <p className="email-description">
              Be the first to know when new chapters are released and discover new interactive stories.
            </p>
            
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="email-input"
            />
          </div>

          {submitError && (
            <div className="error-message">
              {submitError}
            </div>
          )}

          <button 
            type="submit" 
            className={`submit-feedback-btn ${isSubmitting ? 'submitting' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending Feedback...' : 'Share Feedback'}
          </button>
        </form>

      </div>
    </div>
  );
}

function getPathDescription(pathName: string): string {
  const descriptions = {
    'Detective': 'Follow the evidence, trust your training, uncover the truth through investigation.',
    'Conspirator': 'Join them from within, learn their secrets, but risk being corrupted.',
    'Scientist': 'Study and experiment, understand the technology behind memory theft.'
  };
  return descriptions[pathName as keyof typeof descriptions] || '';
}